/**
 * Unattended-inbox context slice — loads the cross-source "Asuntos sin atender"
 * inbox from the client and exposes loading + refresh helpers to the SPA.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { UnattendedInboxResponse } from '@zunelo/shared';
import { emptyUnattendedInbox } from '@zunelo/shared';
import { unattendedInboxClient } from '../../services/clients';
import { unattendedInboxErrorMessage } from '../../services/errors';

export interface UnattendedInboxSliceOptions {
  setErrors: (updater: (prev: Record<string, string | null>) => Record<string, string | null>) => void;
}

export function useUnattendedInboxSlice({ setErrors }: UnattendedInboxSliceOptions) {
  const [unattendedInbox, setUnattendedInbox] = useState<UnattendedInboxResponse>(() => emptyUnattendedInbox());
  const [unattendedInboxLoading, setUnattendedInboxLoading] = useState(false);
  const requestIdRef = useRef(0);

  const refreshUnattendedInbox = useCallback(
    async (force?: boolean) => {
      const requestId = ++requestIdRef.current;
      setUnattendedInboxLoading(true);
      setErrors((prev) => ({ ...prev, unattendedInbox: null }));
      try {
        const res = await unattendedInboxClient.getUnattendedInbox({ fresh: force === true });
        if (requestIdRef.current !== requestId) return;
        setUnattendedInbox(res.data);
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setErrors((prev) => ({
          ...prev,
          unattendedInbox: unattendedInboxErrorMessage(err),
        }));
      } finally {
        if (requestIdRef.current === requestId) setUnattendedInboxLoading(false);
      }
    },
    [setErrors],
  );

  return useMemo(
    () => ({
      unattendedInbox,
      setUnattendedInbox,
      unattendedInboxLoading,
      setUnattendedInboxLoading,
      refreshUnattendedInbox,
    }),
    [unattendedInbox, unattendedInboxLoading, refreshUnattendedInbox],
  );
}